import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const RequestsTable = ({ requests, onViewDetails, onEdit, onConvertToTicket }) => {
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    dateFrom: '',
    dateTo: '',
    searchTerm: ''
  });

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'converted', label: 'Converted to Ticket' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-warning/10 text-warning border-warning/20',
      'approved': 'bg-success/10 text-success border-success/20',
      'in-progress': 'bg-primary/10 text-primary border-primary/20',
      'converted': 'bg-secondary/10 text-secondary border-secondary/20',
      'rejected': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-muted-foreground',
      'medium': 'text-primary',
      'high': 'text-warning',
      'critical': 'text-error'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'low': 'ArrowDown',
      'medium': 'Minus',
      'high': 'ArrowUp',
      'critical': 'AlertTriangle'
    };
    return icons?.[priority] || 'Minus';
  };

  const filteredRequests = requests?.filter(request => {
    if (filters?.status && request?.status !== filters?.status) return false;
    if (filters?.priority && request?.priority !== filters?.priority) return false;
    if (filters?.searchTerm) {
      const searchLower = filters?.searchTerm?.toLowerCase();
      return (request?.requestId?.toLowerCase()?.includes(searchLower) ||
      request?.customerName?.toLowerCase()?.includes(searchLower) || request?.serviceType?.toLowerCase()?.includes(searchLower));
    }
    return true;
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      dateFrom: '',
      dateTo: '',
      searchTerm: ''
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={20} color="var(--color-primary)" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground">Filter Requests</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-4">
          <Input
            label="Search"
            type="search"
            placeholder="Request ID, customer, service..."
            value={filters?.searchTerm}
            onChange={(e) => handleFilterChange('searchTerm', e?.target?.value)}
          />
          <Select
            label="Status"
            placeholder="Filter by status"
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
          />
          <Select
            label="Priority"
            placeholder="Filter by priority"
            options={priorityOptions}
            value={filters?.priority}
            onChange={(value) => handleFilterChange('priority', value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {filteredRequests?.length} of {requests?.length} requests
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            iconName="X"
            iconPosition="left"
          >
            Clear Filters
          </Button>
        </div>
      </div>
      <div className="hidden lg:block bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Request ID</th>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Service Type</th>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Priority</th>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Status</th>
                <th className="px-6 py-4 text-left text-sm font-caption font-medium text-muted-foreground">Created</th>
                <th className="px-6 py-4 text-right text-sm font-caption font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredRequests?.map((request) => (
                <tr key={request?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm font-medium text-foreground">{request?.requestId}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-foreground">{request?.customerName}</p>
                      <p className="text-sm text-muted-foreground">{request?.customerEmail}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">{request?.serviceType}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`flex items-center gap-2 ${getPriorityColor(request?.priority)}`}>
                      <Icon name={getPriorityIcon(request?.priority)} size={16} color="currentColor" />
                      <span className="text-sm font-medium capitalize">{request?.priority}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
                      {request?.statusLabel}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-muted-foreground">{request?.createdDate}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onViewDetails(request)}
                        className="p-2 rounded-lg hover:bg-muted transition-smooth"
                        title="View Details"
                      >
                        <Icon name="Eye" size={18} color="var(--color-foreground)" />
                      </button>
                      <button
                        onClick={() => onEdit(request)}
                        className="p-2 rounded-lg hover:bg-muted transition-smooth"
                        title="Edit Request"
                      >
                        <Icon name="Edit" size={18} color="var(--color-primary)" />
                      </button>
                      {(request?.status === 'approved' || request?.status === 'pending') && (
                        <button
                          onClick={() => onConvertToTicket(request)}
                          className="p-2 rounded-lg hover:bg-success/10 transition-smooth"
                          title="Convert to Ticket"
                        >
                          <Icon name="CheckCircle" size={18} color="var(--color-success)" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="lg:hidden space-y-4">
        {filteredRequests?.map((request) => (
          <div key={request?.id} className="bg-card rounded-lg border border-border shadow-elevation-1 p-4">
            <div className="flex items-start justify-between mb-4">
              <div>
                <span className="font-mono text-sm font-medium text-foreground">{request?.requestId}</span>
                <p className="text-xs text-muted-foreground mt-1">{request?.createdDate}</p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
                {request?.statusLabel}
              </span>
            </div>

            <div className="space-y-3 mb-4">
              <div>
                <p className="text-sm font-caption text-muted-foreground mb-1">Customer</p>
                <p className="font-medium text-foreground">{request?.customerName}</p>
                <p className="text-sm text-muted-foreground">{request?.customerEmail}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-sm font-caption text-muted-foreground mb-1">Service Type</p>
                  <p className="text-sm text-foreground">{request?.serviceType}</p>
                </div>
                <div>
                  <p className="text-sm font-caption text-muted-foreground mb-1">Priority</p>
                  <div className={`flex items-center gap-2 ${getPriorityColor(request?.priority)}`}>
                    <Icon name={getPriorityIcon(request?.priority)} size={14} color="currentColor" />
                    <span className="text-sm font-medium capitalize">{request?.priority}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewDetails(request)}
                iconName="Eye"
                iconPosition="left"
                className="flex-1"
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(request)}
                iconName="Edit"
                iconPosition="left"
                className="flex-1"
              >
                Edit
              </Button>
              {(request?.status === 'approved' || request?.status === 'pending') && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => onConvertToTicket(request)}
                  iconName="CheckCircle"
                  iconPosition="left"
                  className="flex-1"
                >
                  Convert
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {filteredRequests?.length === 0 && (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Requests Found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or search criteria</p>
        </div>
      )}
    </div>
  );
};

export default RequestsTable;